import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  Boxes, Wrench, ClipboardList, ShieldAlert, Radio, ArrowRight, Loader2, CalendarDays, TrendingUp, PenTool,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";
import PpfCommandCenter from "@/components/PpfCommandCenter";

const ORANGE = "#f58220";

/**
 * PLANNER view — fleet-level scheduling & resource overview.
 * Big overview cards, upcoming work, current downtime, shift summary.
 */
export default function PlannerDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get("/assets"),
        api.get("/breakdowns"),
        api.get("/inspections"),
        api.get("/handovers"),
        api.get("/ppfs"),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      setD({
        loading: false,
        assets: list(results[0]),
        breakdowns: list(results[1]),
        inspections: list(results[2]),
        handovers: list(results[3]),
        ppfs: list(results[4]),
      });
    })().catch((ex) => setD({ loading: false, error: apiError(ex) }));
  }, []);

  if (d.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-3 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading fleet overview…
      </div>
    );
  }

  const assetsDown = d.assets.filter((a) => ["down", "degraded", "maintenance"].includes(String(a.status || "").toLowerCase())).length;
  const activeBreakdowns = d.breakdowns.filter((b) => !["closed", "resolved", "done"].includes(String(b.status || "").toLowerCase())).length;
  const upcomingInspections = d.inspections.filter((i) => {
    const s = String(i.status || i.stage || "").toLowerCase();
    return s.includes("scheduled") || s.includes("pending") || s.includes("queued") || s.includes("open");
  }).length;
  const handoversToday = d.handovers.filter((h) => {
    const t = h.created_at || h.date;
    return t && new Date(t).toDateString() === new Date().toDateString();
  }).length;
  const ppfsOpen = d.ppfs.filter((p) => !["closed", "complete", "done"].includes(String(p.status || "").toLowerCase())).length;

  // 14-day activity trend
  const trend = buildTrend(d.breakdowns, d.handovers, d.inspections);
  // upcoming work queue
  const upcoming = [
    ...d.inspections.filter((i) => (i.due_at || i.scheduled_at)).slice(0, 4).map((i) => ({ kind: "inspection", label: i.template_name || i.title, when: i.due_at || i.scheduled_at, site: i.site_code || i.site })),
    ...d.ppfs.filter((p) => (p.due_at || p.due_date)).slice(0, 4).map((p) => ({ kind: "ppf", label: p.title || p.description, when: p.due_at || p.due_date, site: p.site_code || p.site })),
  ].sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0)).slice(0, 6);

  return (
    <div className="space-y-8" data-testid="planner-dashboard">
      {/* Hero */}
      <div className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            <Radio className="w-3.5 h-3.5" style={{ color: ORANGE }} />
            Planner Console · Fleet overview
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mt-2">
            Every asset,<br className="hidden md:block" />
            <span style={{ color: ORANGE }}>every shift.</span>
          </h1>
          <p className="text-muted mt-3 max-w-xl">
            Whole-fleet snapshot for scheduling &amp; resource allocation.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.site_code && <div className="pill">{user.site_code}</div>}
          <div className="pill pill-orange">{user?.role || "planner"}</div>
        </div>
      </div>

      {/* Overview cards — massive */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BigCard label="Total Assets" value={d.assets.length} icon={Boxes} sub={`${assetsDown} not operational`} />
        <BigCard label="Active Breakdowns" value={activeBreakdowns} icon={Wrench} tone={activeBreakdowns > 0 ? "hazard" : undefined} sub={`${d.breakdowns.length} total logged`} />
        <BigCard label="Upcoming Inspections" value={upcomingInspections} icon={ClipboardList} sub={`${d.inspections.length} on file`} />
        <BigCard label="Handovers Today" value={handoversToday} icon={CalendarDays} sub={`${d.handovers.length} rolling`} />
      </div>

      {/* Trend + upcoming work */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="panel-heavy p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">14-day activity</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="planTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ORANGE} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={ORANGE} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke={ORANGE} strokeWidth={2.5} fill="url(#planTrend)" />
            </AreaChart>
          </ResponsiveContainer>
        </section>

        <section className="panel-heavy p-5">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Upcoming work</div>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-muted text-sm py-3">Nothing scheduled.</div>
          ) : (
            <div className="divide-border">
              {upcoming.map((u, i) => (
                <div key={i} className="py-3 first:pt-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="pill pill-orange">{u.kind}</span>
                    {u.site && <span className="pill">{u.site}</span>}
                  </div>
                  <div className="font-display font-black text-sm truncate">{u.label || "Task"}</div>
                  <div className="text-[10px] font-mono-tight text-muted mt-0.5">{tryDate(u.when)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* PPF Command Center — sortable/filterable with 1-tap PDF & Email */}
      <PpfCommandCenter ppfs={d.ppfs} onUpdated={() => {
        // re-fetch ppfs on updates
        api.get("/ppfs").then((r) => {
          const arr = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.results || r.data?.data || [];
          setD((prev) => ({ ...prev, ppfs: arr }));
        }).catch(() => {});
      }} />

      {/* Quick navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickTile to="/assets" label="Assets" icon={Boxes} />
        <QuickTile to="/breakdowns" label="Breakdowns" icon={Wrench} />
        <QuickTile to="/inspections" label="Inspections" icon={ClipboardList} />
        <QuickTile to="/ppfs" label="Planning Requests" icon={PenTool} badge={ppfsOpen} />
      </div>
    </div>
  );
}

function BigCard({ label, value, sub, icon: Icon, tone }) {
  const styles =
    tone === "hazard"
      ? { background: "linear-gradient(180deg, #ffb15c, #f58220)", color: "#0a0a0a", borderColor: "#f58220" }
      : {};
  const iconStyle =
    tone === "hazard"
      ? { background: "#0a0a0a", color: ORANGE }
      : { background: "rgba(245,130,32,0.15)", color: ORANGE, border: "1px solid rgba(245,130,32,0.3)" };
  return (
    <div className="panel-heavy p-5 md:p-6 min-h-[170px] flex flex-col justify-between" style={styles}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black">{label}</div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={iconStyle}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div>
        <div className="font-display font-black text-6xl leading-none tracking-tight">{value ?? "—"}</div>
        {sub && <div className="text-xs uppercase tracking-widest mt-2 opacity-80">{sub}</div>}
      </div>
    </div>
  );
}

function QuickTile({ to, label, icon: Icon, badge }) {
  return (
    <Link to={to} className="panel-heavy p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
           style={{ background: "rgba(245,130,32,0.15)", color: ORANGE, border: "1px solid rgba(245,130,32,0.3)" }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 font-display font-black uppercase tracking-widest text-sm">{label}</div>
      {badge > 0 && <span className="pill pill-orange-solid">{badge}</span>}
      <ArrowRight className="w-4 h-4 text-muted" />
    </Link>
  );
}

function buildTrend(...lists) {
  const days = 14;
  const map = new Map();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    map.set(todayLocalIso(d), 0);
  }
  lists.forEach((L) => (L || []).forEach((it) => {
    const raw = it.created_at || it.date || it.ts;
    if (!raw) return;
    const k = todayLocalIso(new Date(raw));
    if (map.has(k)) map.set(k, map.get(k) + 1);
  }));
  return [...map.entries()].map(([date, total]) => ({ date: date.slice(5), total }));
}
function tryDate(v) { try { return new Date(v).toLocaleDateString(); } catch { return String(v); } }
